import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { PluginFieldType } from '@makyoapp/makyo-sdk/models/Plugins'
import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { IActionFormProps } from '@makyoapp/makyo-common'
import { Button, ButtonVariant, Field, Label, Loading, SelectDropdown, Switch } from '@makyoapp/makyo-ui'

import { useForm } from '@/lib/use-form'
import type { PluginActionField } from '@/hooks/usePluginActions'
import { usePluginCredentials } from '@/hooks/usePluginCredentials'
import { isEmpty } from '@/shared/utils'
import PluginOauth2Modal from '@/pages/org/plugins/PluginOauth2Modal'
import { ActionInputV3 } from '@/components/ActionInputV3/ActionInputV3'
import { Help } from '@/components/Help'

function isValidFieldValue(val: unknown) {
  if (typeof val === 'object' && val !== null) {
    if ('dataType' in val && 'value' in val) {
      return !isEmpty(val.dataType) || !isEmpty(val.value)
    }
    return !isEmpty(val)
  }

  if (typeof val === 'string') {
    return !!val.trim().length
  }

  return true
}

interface I18nField {
  [key: string]: string
  en: string
}

interface FormValues extends Record<string, any> {}

export default function ActFormPlugin({ onSubmit, onDismiss, initialData }: IActionFormProps) {
  const { i18n } = useTranslation()
  const { credentials, loading, refetch } = usePluginCredentials(initialData?.pluginAction?.pluginId)
  const pluginFields = (initialData?.pluginAction?.fields || []) as PluginActionField[]
  const [showOauthModal, setShowOauthModal] = useState(false)

  const defaultValues = pluginFields.reduce<FormValues>(
    (acc, field) => ({
      ...acc,
      [field._Key]: initialData?.[field._Key] || '',
    }),
    {}
  )

  const { form, onSubmit: onFormSubmit } = useForm<FormValues>({
    defaultValues,
    validate(data, setError) {
      pluginFields.forEach((field: PluginActionField) => {
        // Skip validation for boolean fields and optional fields
        if (field._Type === PluginFieldType.Boolean || field._Optional !== false) {
          return
        }

        const value = data[field._Key]?.value ? data[field._Key]?.value : data[field._Key]

        switch (field._Type) {
          case PluginFieldType.Email:
            if (!isValidFieldValue(value)) {
              setError(field._Key as keyof FormValues, `${field._Label.en} is required`)
            }
            break
          case PluginFieldType.Chips:
            if (!isValidFieldValue(value)) {
              setError(field._Key as keyof FormValues, `${field._Label.en} is required`)
            }
            break
          case PluginFieldType.Text:
          case PluginFieldType.Textarea:
            if (!isValidFieldValue(value)) {
              setError(field._Key as keyof FormValues, `${field._Label.en} is required`)
            }
            break
          case PluginFieldType.Select:
            if (!value) {
              setError(field._Key as keyof FormValues, `${field._Label.en} is required`)
            }
            break
        }
      })
    },
  })

  const handleSubmit = onFormSubmit((data) => {
    onSubmit({ ...data, pluginAction: initialData?.pluginAction })
  })

  if (loading) {
    return <Loading />
  }

  const filteredCredentials = [
    {
      name: 'Select credential',
      credentialId: '',
      isPlaceholder: true,
      picture: '',
    },
    ...credentials.map((cred) => ({
      name: cred.label,
      credentialId: cred.id,
      picture: cred.picture,
      working: cred.working,
    })),
    { name: '+ Add account', isAddAccount: true, picture: '' },
  ]

  const invalidForm = pluginFields.some((field: PluginActionField) => {
    // Skip validation for boolean fields and optional fields
    if (field._Type === PluginFieldType.Boolean || field._Optional !== false) {
      return false
    }

    if (field._Type === PluginFieldType.Credentials) {
      return form.getValues(field._Key as keyof FormValues) === ''
    }

    const value = form.getValues(field._Key as keyof FormValues)
    return isEmpty(value)
  })

  const handleAddAccount = () => {
    setShowOauthModal(true)
  }

  return (
    <div>
      <div className='p-4'>
        {/* Render plugin fields dynamically */}
        {pluginFields.map((field: PluginActionField) => (
          <Field key={field._Key}>
            <div className='flex items-center gap-1'>
              <Label required={field._Type !== PluginFieldType.Boolean && field._Optional === false}>
                {(field._Label as I18nField)[i18n.language] || field._Label.en}
              </Label>
              {field._Description && (
                <Help options={{ placement: 'top' }}>
                  {(field._Description as I18nField)[i18n.language] || field._Description.en}
                </Help>
              )}
            </div>

            {field._Type === PluginFieldType.Text && (
              <ActionInputV3
                options={initialData?.actionOptions}
                isActionWorkflow={initialData?.isActionWorkflow}
                // globalTableId={shouldShowTable ? form.getValues('tableId') : undefined}
                placeholder={`Enter ${((field._Label as I18nField)[i18n.language] || field._Label.en).toLowerCase()}`}
                value={form.getValues(field._Key as keyof FormValues)}
                columnTypes={[
                  ColumnTypes.Text,
                  ColumnTypes.Email,
                  ColumnTypes.Phone,
                  ColumnTypes.URL,
                  ColumnTypes.RichText,
                ]}
                onChange={({ ...value }) => {
                  form.setValues(field._Key as keyof FormValues, value)
                }}
                error={form.errors?.[field._Key as keyof FormValues]}
              />
            )}

            {field._Type === PluginFieldType.Textarea && (
              <ActionInputV3
                options={initialData?.actionOptions}
                isActionWorkflow={initialData?.isActionWorkflow}
                // globalTableId={shouldShowTable ? form.getValues('tableId') : undefined}
                placeholder={`Enter ${((field._Label as I18nField)[i18n.language] || field._Label.en).toLowerCase()}`}
                inputType='textarea'
                value={form.getValues(field._Key as keyof FormValues)}
                columnTypes={[ColumnTypes.Text, ColumnTypes.RichText]}
                onChange={({ ...value }) => {
                  form.setValues(field._Key as keyof FormValues, value)
                }}
                error={form.errors?.[field._Key as keyof FormValues]}
              />
            )}

            {field._Type === PluginFieldType.Chips && (
              <ActionInputV3
                options={initialData?.actionOptions}
                isActionWorkflow={initialData?.isActionWorkflow}
                // globalTableId={shouldShowTable ? form.getValues('tableId') : undefined}
                placeholder={`Enter comma-separated ${((field._Label as I18nField)[i18n.language] || field._Label.en).toLowerCase()}`}
                value={form.getValues(field._Key as keyof FormValues)}
                columnTypes={[ColumnTypes.Text, ColumnTypes.Email, ColumnTypes.Phone, ColumnTypes.URL]}
                onChange={({ ...value }) => {
                  form.setValues(field._Key as keyof FormValues, value)
                }}
                error={form.errors?.[field._Key as keyof FormValues]}
              />
            )}

            {field._Type === PluginFieldType.Boolean && (
              <div className='flex items-center gap-3'>
                <div className='flex-shrink-0'>
                  <Switch
                    checked={!!form.getValues(field._Key as keyof FormValues)}
                    onClick={(isChecked) => form.setValues(field._Key as keyof FormValues, isChecked)}
                  />
                </div>
              </div>
            )}

            {field._Type === PluginFieldType.Credentials && (
              <SelectDropdown
                menuContainerClassName='mk-ag-sdd'
                options={filteredCredentials}
                optionLabel={(opt) => ((opt as any).isAddAccount ? opt.name : `${opt.name}`)}
                value={filteredCredentials.find(
                  (cred) => cred.credentialId === form.getValues(field._Key as keyof FormValues)
                )}
                itemClassName={(i: any) => (i?.isPlaceholder ? 'hidden!' : '')}
                onChange={(cred) => {
                  if ((cred as any).isAddAccount) {
                    handleAddAccount()
                    return
                  }

                  form.setValues(field._Key as keyof FormValues, cred.credentialId)
                }}
                renderOption={(cred, _, __, idx) => {
                  if ((cred as any).isPlaceholder) {
                    return (
                      <div className='w-full flex items-center text-sm rounded-md opacity-60 select-none'>
                        <div className='flex-1 text-gray-400 dark:text-gray-500'>
                          {cred.name || 'Select an account...'}
                        </div>
                      </div>
                    )
                  }

                  if ((cred as any).isAddAccount) {
                    return (
                      <div className='w-full flex items-center text-sm rounded-md border border-dashed border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20 cursor-pointer py-2 px-4'>
                        <div className='flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-800 mr-3'>
                          <svg
                            className='w-4 h-4 text-blue-600 dark:text-blue-400'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M12 6v6m0 0v6m0-6h6m-6 0H6'
                            />
                          </svg>
                        </div>
                        <div className='flex-1'>
                          <span className='font-medium text-blue-700 dark:text-blue-300'>{cred.name}</span>
                          <div className='text-xs text-blue-600 dark:text-blue-400 mt-0.5'>Connect a new account</div>
                        </div>
                      </div>
                    )
                  }

                  return (
                    <div className='flex items-center w-full px-2'>
                      <div className='flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 mr-3 flex-shrink-0'>
                        {cred.picture ? (
                          <img
                            src={cred.picture}
                            alt={cred.name || 'Account'}
                            className='w-8 h-8 rounded-full object-cover'
                            onError={(e) => {
                              // Hide the image and show fallback SVG
                              const target = e.currentTarget
                              target.onerror = null
                              target.style.display = 'none'
                              // Insert fallback SVG after the image
                              const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
                              svg.setAttribute('class', 'w-4 h-4 text-gray-600 dark:text-gray-400')
                              svg.setAttribute('fill', 'none')
                              svg.setAttribute('stroke', 'currentColor')
                              svg.setAttribute('viewBox', '0 0 24 24')
                              svg.innerHTML = `
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                />
                              `
                              target.parentNode?.appendChild(svg)
                            }}
                          />
                        ) : (
                          <svg
                            className='w-4 h-4 text-gray-600 dark:text-gray-400'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
                            />
                          </svg>
                        )}
                      </div>
                      <div className='flex-1 min-w-0'>
                        <div className='font-medium text-gray-900 dark:text-gray-100 truncate'>{cred.name}</div>
                      </div>
                      <div className='flex-shrink-0 ml-2'>
                        <div
                          className={`w-2 h-2 rounded-full ${(cred as any).working ? 'bg-green-400' : 'bg-yellow-400'}`}
                        ></div>
                      </div>
                    </div>
                  )
                }}
                disabled={loading}
                placeholder='Select credential'
              />
            )}

            {field._Type === PluginFieldType.Select && field._Options && (
              <ActionInputV3
                options={initialData?.actionOptions}
                isActionWorkflow={initialData?.isActionWorkflow}
                // globalTableId={shouldShowTable ? form.getValues('tableId') : undefined}
                value={form.getValues(field._Key as keyof FormValues)}
                columnTypes={[
                  ColumnTypes.Text,
                  ColumnTypes.Email,
                  ColumnTypes.Phone,
                  ColumnTypes.URL,
                  ColumnTypes.RichText,
                ]}
                selectOptions={field._Options.map((opt: string) => ({ label: opt, value: opt }))}
                onChange={({ ...value }) => {
                  form.setValues(field._Key as keyof FormValues, value)
                }}
                error={form.errors?.[field._Key as keyof FormValues]}
              />
            )}
          </Field>
        ))}
      </div>

      <div className='px-4 py-2 border-t bdr flex justify-between items-center'>
        <Button variant={ButtonVariant.Outlined} onClick={onDismiss}>
          <Trans>Cancel</Trans>
        </Button>
        <Button onClick={handleSubmit} disabled={invalidForm}>
          <Trans>Save</Trans>
        </Button>
      </div>

      {/* OAuth2 Modal */}
      {initialData?.pluginAction?.pluginDetail && (
        <PluginOauth2Modal
          plugin={initialData.pluginAction.pluginDetail}
          show={showOauthModal}
          onClose={() => setShowOauthModal(false)}
          onSuccess={() => {
            // Refetch credentials to get the newly added one
            refetch()
            setShowOauthModal(false)
          }}
        />
      )}
    </div>
  )
}
